import { z } from 'zod';
import { getApiHost } from './api_host';
import {
  generateRequestId,
  ServiceError,
  ServiceHttpError,
} from './service_error';
import { InferFromSchema, zodParse } from './zod_parse';

const extensionConfigSchema = z.object({
  enableSvgIconEmbed: z.boolean().nullish(),
  enableLibrary: z.boolean().nullish(),
  enableSpacingSetting: z.boolean().nullish(),
  enableSpacingAutomation: z.boolean().nullish(),
  enableInternalLibraryPublish: z.boolean().nullish(),
  freeComponentSlugs: z.array(z.string()),
});
export type ExtensionConfig = z.TypeOf<typeof extensionConfigSchema>;

type GetUserRequest = {
  chromeExtensionVersion?: number;
};
const getUserResponseSchema = z.object({
  status: z.literal('success'),
  user: z.object({
    name: z.string(),
    plan: z.enum(['client', 'starter', 'creator', 'team']),
    id: z.string(),
  }),
  chromeExtensionsConfig: extensionConfigSchema.nullish(),
});
export type GetUserResponse = z.TypeOf<typeof getUserResponseSchema>;

export interface AuthService {
  getUser(req: GetUserRequest): Promise<GetUserResponse>;
}

export class HttpAuthService implements AuthService {
  constructor(private readonly http: HttpEngine) {}

  async getUser(req: GetUserRequest = {}): Promise<GetUserResponse> {
    const params = new URLSearchParams({
      jwt: await this.http.getTokenLegacy(),
    });
    if (req.chromeExtensionVersion != null) {
      params.set('chrome_extension_version', `${req.chromeExtensionVersion}`);
    }
    return this.http.request(
      'GET',
      `/hosted/v1/user/?${params}`,
      undefined,
      getUserResponseSchema,
    );
  }
}

/** Makes authenticated http requests to the relume backend. */
export class HttpEngine {
  constructor(
    private readonly tokenOrGetToken: () => PromiseLike<string> | string,
    private readonly getDistinctId: (() => string | null) | undefined,
    private readonly onHttpError?: (e: ServiceHttpError) => void,
  ) {}

  /**
   * The engine already adds the Authorization header.
   * New endpoints should use that only.
   */
  readonly getTokenLegacy = async (): Promise<string> => {
    return await this.tokenOrGetToken();
  };

  /* Send raw requests without JSON serialisation */
  async requestRaw<T extends z.ZodType<any, z.ZodTypeDef, any>>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    data: BodyInit | undefined,
    headerEntries: [string, string][],
    schema?: T,
  ) {
    url = getApiHost() + url.replace(/^\/+/, '');

    const requestId = generateRequestId();
    const headers = Object.fromEntries([
      ['x-request-id', requestId],
      ...headerEntries,
    ]);

    const distinctId = this.getDistinctId?.();
    if (distinctId) {
      headers['x-distinct-id'] = distinctId;
    }

    const token = await this.getTokenLegacy();
    if (token) {
      headers['authorization'] = 'Bearer ' + token;
    }
    const request: RequestInit = {
      method,
      headers,
      body: data,
    };

    try {
      const r = await fetch(url, request).catch((error) => {
        console.error(error);
        // Fetch only throws on network errors.  Rewrite them to have a
        // more user-friendly message.
        throw new ServiceError(
          'Network error.  Check your internet connection.',
          requestId,
        );
      });
      if (!r.ok) {
        const responseText = await r.text();
        let detail;
        try {
          ({ detail } = JSON.parse(responseText));
        } catch (e) {}
        const error = new ServiceHttpError(
          r.status,
          responseText,
          requestId,
          detail,
        );
        this.onHttpError?.(error);
        throw error;
      }
      if (schema) {
        return zodParse(schema, await r.json());
      } else {
        return await r.json();
      }
    } catch (e) {
      if (e instanceof ServiceError) throw e;
      throw new ServiceError(e, requestId);
    }
  }

  // Request has 2 forms, with a schema in which case it'll attempt to parse the
  // response against it (first overload) and with no schema in which case it returns a object
  // of unknown type (second overload)
  async request<T extends z.ZodType<any, z.ZodTypeDef, any>>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    data: unknown | undefined,
    schema: T,
    abortSignal?: AbortSignal,
  ): Promise<InferFromSchema<T>>;
  // eslint-disable-next-line no-dupe-class-members
  async request(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    data: unknown | undefined,
    abortSignal?: AbortSignal,
  ): Promise<unknown>;
  // We leave off abort signal from this overload to avoid having multiple
  // optional parameters. This form is usually only used when we don't care
  // about the response anyway.
  // eslint-disable-next-line no-dupe-class-members
  async request<T extends z.ZodType<any, z.ZodTypeDef, any>>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    data: unknown | undefined,
    schema?: T,
  ): Promise<InferFromSchema<T> | unknown> {
    const headers: [string, string][] = [];
    if (data) {
      headers.push(['content-type', 'application/json']);
    }
    return this.requestRaw(
      method,
      url,
      data ? JSON.stringify(data) : undefined,
      headers,
      schema,
    );
  }
}
